/**
 * Turbo (Hotwire) lifecycle integration for EConnect custom elements.
 *
 * This module provides automatic state cleanup for applications using Hotwire Turbo navigation.
 * Import and call `setupTurboLifecycle()` in your application initialization to enable Turbo support.
 *
 * @example
 * ```typescript
 * // In your app entry point (e.g., main.ts, app.ts)
 * import { register, setupTurboLifecycle } from '@econnect/webcomponents';
 *
 * register();
 * setupTurboLifecycle();
 * ```
 *
 * @module turboLifecycle
 */

import { useStateStore } from '../composables/useStateStore';

/**
 * Global flag to ensure Turbo lifecycle is only setup once.
 * Prevents duplicate event listeners on hot module replacement or multiple calls.
 */
declare global {
  interface Window {
    __econnect_turbo_installed__?: boolean;
  }
}

/**
 * Sets up Turbo lifecycle event handlers for proper component state cleanup.
 *
 * This function registers an event listener for `turbo:before-cache`.
 * When Turbo is about to cache the page, this handler clears transient component state from the store.
 *
 * **Why this is needed:**
 * - Turbo caches the DOM before navigation, keeping elements connected
 * - `disconnectedCallback` rarely fires during Turbo navigation
 * - Transient state accumulates across navigations without explicit cleanup
 *
 * **When to use:**
 * - Your application uses Hotwire Turbo for navigation
 * - You're experiencing performance degradation on client-side navigation
 *
 * **When NOT to use:**
 * - Full page reloads (browser handles cleanup automatically)
 * - Server-rendered pages without client-side navigation
 *
 * @param options Configuration options
 * @param options.verbose Enable console logging for debugging (default: false)
 *
 * @example
 * ```typescript
 * // Basic setup
 * setupTurboLifecycle();
 *
 * // With verbose logging for debugging
 * setupTurboLifecycle({ verbose: true });
 * ```
 *
 * @returns Cleanup function to remove event listeners (useful for testing)
 */
export function setupTurboLifecycle(options?: { verbose?: boolean }): () => void {
  const verbose = options?.verbose ?? false;

  if (typeof document === 'undefined') {
    throw new TypeError('[EConnect] setupTurboLifecycle requires a browser environment');
  }

  // Idempotency check: only install once
  if (globalThis.window?.__econnect_turbo_installed__) {
    if (verbose) {
      console.log('[EConnect] Turbo lifecycle already installed, skipping');
    }
    return () => {}; // Return no-op cleanup
  }

  if (globalThis.window === undefined || !('Turbo' in globalThis.window)) {
    console.warn(
      '[EConnect] Turbo not detected. setupTurboLifecycle() has no effect. ' +
        'Make sure Turbo is loaded before calling this function.'
    );
  }

  const stateStore = useStateStore();
  const clearState = () => {
    stateStore.clear();
    if (verbose) {
      console.log('[EConnect] Cleared transient component state');
    }
  };

  // Register event listener for cache event
  document.addEventListener('turbo:before-cache', clearState);

  // Mark as installed
  if (globalThis.window) {
    globalThis.window.__econnect_turbo_installed__ = true;
  }

  if (verbose) {
    console.log('[EConnect] Turbo lifecycle handlers registered');
  }

  // Return cleanup function
  return () => {
    document.removeEventListener('turbo:before-cache', clearState);
    if (globalThis.window) {
      delete globalThis.window.__econnect_turbo_installed__;
    }
    if (verbose) {
      console.log('[EConnect] Turbo lifecycle handlers removed');
    }
  };
}

/**
 * Check if Turbo is available in the current environment.
 *
 * @returns true if Turbo is detected, false otherwise
 *
 * @example
 * ```typescript
 * if (isTurboAvailable()) {
 *   setupTurboLifecycle();
 * }
 * ```
 */
export function isTurboAvailable(): boolean {
  return globalThis.window !== undefined && 'Turbo' in globalThis.window;
}

/**
 * Conditionally sets up Turbo lifecycle if Turbo is detected.
 *
 * This is a convenience function that checks for Turbo availability
 * before setting up lifecycle handlers. Safe to call in any environment.
 *
 * @param options Configuration options
 * @returns Cleanup function if setup was successful, undefined otherwise
 *
 * @example
 * ```typescript
 * // Automatically setup if Turbo is available
 * setupTurboLifecycleIfAvailable({ verbose: true });
 * ```
 */
export function setupTurboLifecycleIfAvailable(options?: {
  verbose?: boolean;
}): (() => void) | undefined {
  if (!isTurboAvailable()) {
    if (options?.verbose) {
      console.log('[EConnect] Turbo not detected, skipping lifecycle setup');
    }
    return undefined;
  }
  return setupTurboLifecycle(options);
}
